import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import { currentLanguageId } from '../i18n';
import i18n from "../i18n";

/**
 * fuarService.js — Fuar, Fuar Serisi ve fuara özel Talep Türleri
 *
 * CANLI (mock yok). Uçların hepsi `FuarController` üzerinde ve bu denetleyicinin
 * yerel auth bypass'ı YOK: gerçek bir oturum çifti (X-user-id + X-token) olmadan 401.
 * Rehberde bu uçlar belgelenmemiş (FE-README "Not covered: FuarController outside the
 * rule and job-type endpoints") — sözleşme doğrudan backend kaynağından okundu, bkz.
 * util/knowledgebase/fuar-seri-ve-talep-turleri.md.
 *
 * ⚠️ 2026-09-15 CANLI HATA: seri okuyan iki uç 500 dönüyor —
 *    `GET /fuar/series/{id}` ve seriye bağlı bir fuarın `GET /fuar/{id}/detay-new`'ü
 *    ("Boolean cannot be cast to Integer": FuarSeriesEntity is_deleted'ı (int) ile
 *    cast ediyor). `getFuarDetail` bu yüzden detay-classic'e DÜŞER ve sonucu
 *    `seriesReadFailed: true` ile işaretler; ekran seriyi listeden adlandırır ama
 *    düzenlemeyi kapatır (PUT texts upsert'i okuyamadığımız hikâye metnini ezerdi).
 */

const FUAR_API = `${JAVA_BACKEND_API_URL}/fuar`;

export const LANGUAGE = { TR: 1, EN: 2 };

// ─── Fuar logosu (backlog #18/#20) ────────────────────────────────────────────
// Backend her fuar için `fuarLogoUrl` veriyor: LEGACY'de mutlak (eski PHP sunucusu),
// UPLOADED/DEFAULT'ta backend kökenine göre göreli — /public/files/... /api/v1'in DIŞINDA.
const BACKEND_ORIGIN = JAVA_BACKEND_API_URL.replace(/\/api\/v1$/, '');
const LEGACY_LOGO_BASE = 'https://fds.sakagrup.com/repo/fuarlogos/';
export const FUAR_LOGO_DEFAULT_SRC = `${BACKEND_ORIGIN}/public/files/fuar_logos/default`;

/**
 * `fuarLogoUrl` → gösterilebilir src. `fileName` (ham fuar_logo) yalnızca fuarLogoUrl
 * dönmeyen eski bir backend için: o durumda ad eski PHP sunucusunda aranır.
 */
export function fuarLogoSrc(fuarLogoUrl, fileName) {
  if (fuarLogoUrl) {
    if (/^https?:\/\//i.test(fuarLogoUrl)) return fuarLogoUrl;
    return `${BACKEND_ORIGIN}${fuarLogoUrl.startsWith('/') ? '' : '/'}${fuarLogoUrl}`;
  }
  if (fileName) return `${LEGACY_LOGO_BASE}${fileName}`;
  return FUAR_LOGO_DEFAULT_SRC;
}

/**
 * Hata gövdeleri üç şekilde geliyor (düz metin, {message,result,data}, boş) — README
 * "branch on the status code, not the body". Ekrana basılabilir tek bir string üret.
 */
export function apiErrorMessage(error, fallback = i18n.t("common:bilinmeyenHata")) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (status === 401) return i18n.t("services:fuarService.oturumGecersiz401LutfenYeniden");
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object' && data.message) return data.message;
  if (typeof error === 'string') return error;
  return error?.message || fallback;
}

/** GenericResultJson başarısızlığı HTTP 200 + result:false olabilir — onu da hata say. */
function unwrapResult(data, fallback) {
  if (data && typeof data === 'object' && data.result === false) {
    throw new Error(data.message || fallback);
  }
  return data;
}

/** "… with ID: 123" — add-new ve series create yeni id'yi yalnızca mesajda taşıyor. */
export function parseIdFromMessage(message) {
  const m = /ID:\s*(\d+)/i.exec(message || '');
  return m ? Number(m[1]) : null;
}

export const fuarService = {
  // ─── Fuar ──────────────────────────────────────────────────────────────────

  /**
   * GET /fuar/list?languageId=1[&group=] — sayfalama/arama PARAMETRESİ YOK, düz dizi döner.
   * Arama ve sayfalama ekranda (FuarListPage) yapılıyor.
   *
   * Backlog #45 (2026-09-18): backend artık `group` gönderilmeyince ARŞİVLENMİŞ fuarları
   * DIŞLIYOR (FuarGroupConstant'ın belgesiyle tutarlı hale getirildi — eskiden tutarsızdı).
   * FuarListPage tek seferde arşiv dâhil TÜM fuarları çekip ekranda gruplamaya devam ediyor,
   * bu yüzden `group: 'ALL'` — backend'in "gerçekten her şey" sentinel'i — burada varsayılan.
   */
  // WAS (until #332): languageId = LANGUAGE.TR -- now the language on screen (fair names fall back to TR).
  async getFuarList({ languageId = currentLanguageId(), group = 'ALL' } = {}) {
    const response = await axios.get(`${FUAR_API}/list`, {
      params: group ? { languageId, group } : { languageId },
      headers: getAuthHeader(),
    });
    return Array.isArray(response.data) ? response.data : (response.data?.content || []);
  },

  /**
   * GET /fuar/selectable?languageId=1 — backlog #187. Oturumdaki kullanıcının SEÇEBİLECEĞİ
   * fuarlar; kimin için olduğu parametreyle değil oturumdan belirleniyor. Firma hesabı
   * (grup 9/19 ya da grupsuz) yalnızca firmasının bağlı olduğu fuarları alır (firma_to_fuar
   * herhangi bir rolde ya da silinmemiş bir firma_to_stand standı); personel `/fuar/list` ile
   * birebir aynı listeyi alır. Öğeler `/fuar/list`'inkilerle aynı şekilde, arşivli/silinmiş
   * fuarlar hariç, en yeni önce. Bağlı fuarı olmayan firma → [].
   */
  // WAS (until #332): languageId = LANGUAGE.TR -- now the language on screen.
  async getSelectableFuarList({ languageId = currentLanguageId() } = {}) {
    const response = await axios.get(`${FUAR_API}/selectable`, {
      params: { languageId },
      headers: getAuthHeader(),
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * PUT /users/updateUsersActiveFuar {fuarId} — kullanıcının seçili fuarı (user.fuar_id). Kimin
   * seçiminin değiştiği gövdeden değil oturumdan alınır (#43). #187: firma hesabı
   * `getSelectableFuarList` dışında bir fuar seçerse 403 + İngilizce düz metin döner; ekrana
   * basılabilsin diye Türkçe bir hataya çevrilir. Personelin seçimi kısıtsız.
   */
  async setActiveFuar(fuarId) {
    try {
      const response = await axios.put(
        `${JAVA_BACKEND_API_URL}/users/updateUsersActiveFuar`,
        { fuarId },
        { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } },
      );
      return response.data;
    } catch (error) {
      if (error?.response?.status === 403) {
        throw new Error(i18n.t("services:fuarService.buFuarFirmanizinBagliOldugu"));
      }
      throw error;
    }
  },

  /**
   * GET /fuar/{id}/detay-new — detay-classic + fuarSeries + hizmet firmaları.
   * 500'de detay-classic'e düşer (bkz. dosya başı); `seriesReadFailed` bayrağı ekranın
   * seri düzenlemesini kapatması için.
   */
  async getFuarDetail(fuarId) {
    try {
      const response = await axios.get(`${FUAR_API}/${fuarId}/detay-new`, { headers: getAuthHeader() });
      return { ...response.data, seriesReadFailed: false };
    } catch (error) {
      if (error?.response?.status !== 500) throw error;
      const classic = await axios.get(`${FUAR_API}/${fuarId}/detay-classic`, { headers: getAuthHeader() });
      return {
        ...classic.data,
        fuarSeries: null,
        seriesReadFailed: true,
        detayNewError: apiErrorMessage(error),
      };
    }
  },

  /** GET /fuar/listFuarAlani → [{ id, fuarAlaniName, fuarAlaniHMax }] */
  async getFuarAlanlari() {
    const response = await axios.get(`${FUAR_API}/listFuarAlani`, { headers: getAuthHeader() });
    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * POST /fuar/logo (multipart `file`) — backlog #18. Logo fuardan ÖNCE yüklenir; dönen
   * `fileName` add-new (zorunlu) / update-new (isteğe bağlı) çağrısına `fuarLogoFile` olarak
   * gider. png/jpg/jpeg/webp, en fazla 5 MB; aksi 400. → { fileName, fuarLogoUrl }
   */
  async uploadFuarLogo(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${FUAR_API}/logo`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    });
    const data = unwrapResult(response.data, i18n.t("services:fuarService.logoYuklenemedi"));
    if (!data?.data?.fileName) throw new Error(i18n.t("services:fuarService.logoYuklendiAmaDosyaAdi"));
    return data.data;
  },

  /**
   * POST /fuar/add-new (multipart). Başarıda 201 + { message, data: { fuarId } }.
   * ⚠️ `fuarLogoFile` zorunlu (bkz. uploadFuarLogo); eski `fuarLogo` dosya parçası 400.
   */
  async addFuar(formData) {
    const response = await axios.post(`${FUAR_API}/add-new`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    });
    const data = unwrapResult(response.data, i18n.t("services:fuarService.fuarOlusturulamadi"));
    return { ...data, fuarId: data?.data?.fuarId ?? null };
  },

  /**
   * PUT /fuar/update-new/{id} (multipart).
   * ⚠️ `fuarSeriesId` GÖNDERİLMEZSE fuar seriden AYRILIR (backend null'ı "unlink" sayar).
   * ⚠️ `organizatorIds` ve hizmet firması listeleri gönderilmezse DOKUNULMAZ —
   *    boş değer göndermek ise o rolü tamamen siler.
   * Backlog #17'nin isteğe bağlı saat / dijital belge alanları: gönderilmezse saklı değer
   * korunur, boş gönderilirse temizlenir. `fuarLogoFile` gönderilmezse mevcut logo kalır.
   */
  async updateFuar(fuarId, formData) {
    const response = await axios.put(`${FUAR_API}/update-new/${fuarId}`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    });
    return { ...unwrapResult(response.data, i18n.t("services:fuarService.fuarGuncellenemedi")), fuarId };
  },

  /**
   * GET /fuar/{id}/silme-kontrolu (#549) — may the fair be deleted? `{ message, data: { silinebilir, engeller: [{ tur, sayi, metin }] } }`,
   * the message in the request's language. 403 without page.fuar, 404 no such / already deleted fair. Raw body: KontrolluSilDugmesi reads it.
   */
  async getFuarSilmeKontrolu(fuarId) {
    const response = await axios.get(`${FUAR_API}/${fuarId}/silme-kontrolu`, { headers: getAuthHeader() });
    return response.data;
  },

  /** DELETE /fuar/delete-soft/{id} — #549: only an EMPTY fair; 409 (same data as silme-kontrolu) when it still has content. */
  async deleteFuar(fuarId) {
    const response = await axios.delete(`${FUAR_API}/delete-soft/${fuarId}`, { headers: getAuthHeader() });
    return unwrapResult(response.data, 'Fuar silinemedi.');
  },

  /** GET /fuar/{id}/status */
  async getFuarStatus(fuarId) {
    const response = await axios.get(`${FUAR_API}/${fuarId}/status`, { headers: getAuthHeader() });
    return response.data;
  },

  /**
   * PUT /fuar/{id}/archive — backlog #16. Fuar her zamanki FUTURE/ACTIVE/CURRENT_YEAR/
   * PREVIOUS_YEAR/PAST grubundan çıkar, yalnızca ?group=ARCHIVED ile görünür.
   */
  async archiveFuar(fuarId) {
    const response = await axios.put(`${FUAR_API}/${fuarId}/archive`, null, { headers: getAuthHeader() });
    return unwrapResult(response.data, i18n.t("services:fuarService.fuarArsivlenemedi"));
  },

  /** PUT /fuar/{id}/unarchive */
  async unarchiveFuar(fuarId) {
    const response = await axios.put(`${FUAR_API}/${fuarId}/unarchive`, null, { headers: getAuthHeader() });
    return unwrapResult(response.data, i18n.t("services:fuarService.fuarArsivdenCikarilamadi"));
  },

  // ─── Fuar Serisi (fuar_series + fuar_series_text) ─────────────────────────
  // Bir serinin her yıl açılan fuarları (ör. "EMITT" → EMITT 2025, EMITT 2026).
  // Metinler dil başına: { languageId, fuarSeriesName, fuarSeriesBackstory }.

  /**
   * GET /fuar/series/list?languageId= → [{ fuar_series_id, fuar_series_link,
   * is_deleted, fuar_series_name }] (snake_case, silinmişler hariç, ada göre sıralı).
   * `fuar_series_name` o dilde metin yoksa null.
   */
  async listFuarSeries(languageId = LANGUAGE.TR) {
    const response = await axios.get(`${FUAR_API}/series/list`, {
      params: { languageId },
      headers: getAuthHeader(),
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * TR + EN listelerini tek satıra birleştirir: [{ id, link, nameTr, nameEn }].
   * Seri detayı okunamadığında (500) ekranın elindeki tek kaynak bu.
   */
  async listFuarSeriesMerged() {
    const [tr, en] = await Promise.all([
      this.listFuarSeries(LANGUAGE.TR),
      this.listFuarSeries(LANGUAGE.EN),
    ]);
    const enById = new Map(en.map((r) => [Number(r.fuar_series_id), r.fuar_series_name]));
    return tr.map((r) => ({
      id: Number(r.fuar_series_id),
      link: r.fuar_series_link || '',
      nameTr: r.fuar_series_name || '',
      nameEn: enById.get(Number(r.fuar_series_id)) || '',
    }));
  },

  /**
   * GET /fuar/series/{id} → { fuarSeries: {id, fuarSeriesLink, isDeleted},
   * fuarSeriesTexts: [...], galleryPhotos: [] }. 404 = yok ya da silinmiş.
   * ⚠️ 2026-09-15 itibarıyla her mevcut seri için 500 (bkz. dosya başı).
   */
  async getFuarSeries(seriesId) {
    const response = await axios.get(`${FUAR_API}/series/${seriesId}`, { headers: getAuthHeader() });
    return response.data;
  },

  /** Form durumundan backend gövdesine: texts dil id'si ile anahtarlanıyor. */
  buildSeriesBody({ link, nameTr, backstoryTr, nameEn, backstoryEn }) {
    return {
      fuarSeriesLink: link?.trim() || null,
      texts: {
        1: { languageId: LANGUAGE.TR, fuarSeriesName: nameTr?.trim() || '', fuarSeriesBackstory: backstoryTr?.trim() || null },
        2: { languageId: LANGUAGE.EN, fuarSeriesName: nameEn?.trim() || '', fuarSeriesBackstory: backstoryEn?.trim() || null },
      },
    };
  },

  /** POST /fuar/series — 201 {message:"… with ID: N"}. Yeni id döner. */
  async createFuarSeries(form) {
    const response = await axios.post(`${FUAR_API}/series`, this.buildSeriesBody(form), { headers: getAuthHeader() });
    const data = unwrapResult(response.data, i18n.t("services:fuarService.seriOlusturulamadi"));
    return { ...data, seriesId: parseIdFromMessage(data?.message) };
  },

  /**
   * PUT /fuar/series/{id} — link'i ve gönderilen her dilin metnini UPSERT eder.
   * ⚠️ Backstory null gönderilirse mevcut metin SİLİNİR: gövde tam hâl olmalı.
   */
  async updateFuarSeries(seriesId, form) {
    const response = await axios.put(`${FUAR_API}/series/${seriesId}`, this.buildSeriesBody(form), { headers: getAuthHeader() });
    return unwrapResult(response.data, i18n.t("services:fuarService.seriGuncellenemedi"));
  },

  /** DELETE /fuar/series/delete-soft/{id} — bağlı fuarlar fuar_series_id'lerini KORUR. */
  async deleteFuarSeries(seriesId) {
    const response = await axios.delete(`${FUAR_API}/series/delete-soft/${seriesId}`, { headers: getAuthHeader() });
    return unwrapResult(response.data, 'Seri silinemedi.');
  },

  // ─── Fuara özel talep türleri (stand_job_type_to_fuar) ────────────────────
  // Opt-in allow-list: fuarın HİÇ satırı yoksa tüm katalog açık; tek bir satır bile
  // varsa YALNIZCA listedekiler. Bkz. FE-StandTalepRequestController-guide.md
  // "Per-fair catalogue filter".

  /**
   * GET /fuar/allJobTypes → [{ stand_job_type_id, stand_job_type_name, talep_group,
   * stand_job_type_string, content_approve_needed, ... }] — ada göre sıralı. Auth yok.
   */
  async getAllJobTypes() {
    const response = await axios.get(`${FUAR_API}/allJobTypes`, { headers: getAuthHeader() });
    return Array.isArray(response.data) ? response.data : [];
  },

  /** GET /fuar/{id}/job-types → [{ standJobTypeId, standJobTypeName, color }] */
  async getFuarJobTypes(fuarId) {
    const response = await axios.get(`${FUAR_API}/${fuarId}/job-types`, { headers: getAuthHeader() });
    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * POST /fuar/{id}/job-types { standJobTypeIds } — sil + yeniden yaz (transactional).
   * ⚠️ `[]` "hiçbir tür" DEĞİL, "kısıtlama yok, tüm katalog" demek.
   * ⚠️ stand_job_color her satırda "" olarak yeniden yazılır (eski renkler kaybolur).
   */
  async setFuarJobTypes(fuarId, standJobTypeIds) {
    const response = await axios.post(
      `${FUAR_API}/${fuarId}/job-types`,
      { standJobTypeIds: standJobTypeIds.map(Number) },
      { headers: getAuthHeader() }
    );
    return unwrapResult(response.data, i18n.t("services:fuarService.talepTurleriKaydedilemedi"));
  },
};
